/**
 * Internal tRPC setup for apps/pet-web standalone mode.
 *
 * The auth token comes from the `?token=` the tracked-link redirect appends — see
 * `./petParentToken.ts`. Components exposed via Module Federation must NOT import this module —
 * they should use useAstroPetTRPC() from ./trpcContext.tsx instead, so they keep using the host's
 * token rather than this one.
 */
import { QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, splitLink, wsLink, createWSClient } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { PetAppRouter } from '@astro/api/router';
import { deriveApiUrl, deriveWsUrl } from './deriveApiUrls';
import { capturePetParentToken, getPetParentToken } from './petParentToken';

export const queryClient = new QueryClient();

// Before any client is built, so the first request already carries the token. Reads the URL the
// tab was opened with, which is where the tracked-link redirect leaves it.
capturePetParentToken();

// Derived from the serving origin, not from VITE_* alone: those are inlined at
// build time, so a single promoted image would otherwise point every
// environment at whichever one it was built for. See deriveApiUrls.ts.
const apiUrl = deriveApiUrl();
const wsUrl = deriveWsUrl(apiUrl);

let timezone = 'America/Chicago';
try {
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
} catch {
  // fallback
}

const wsClient = createWSClient({
  url: () => {
    // A websocket upgrade cannot carry custom headers, so the token travels as a query param.
    // `createPetParentContext` reads both. Resolved per connection rather than captured once, so a
    // reconnect after the token is stored still authenticates.
    const token = getPetParentToken();
    const authParam = token ? `&authorization=${encodeURIComponent(token)}` : '';
    return `${wsUrl}?timezone=${encodeURIComponent(timezone)}${authParam}`;
  },
  lazy: { enabled: true, closeMs: 0 },
  retryDelayMs: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 30000),
  keepAlive: {
    enabled: true,
    intervalMs: 30000,
    pongTimeoutMs: 5000,
  },
});

const trpcClient = createTRPCClient<PetAppRouter>({
  links: [
    splitLink({
      condition: (op) => op.type === 'subscription',
      true: wsLink<PetAppRouter>({ client: wsClient }),
      false: httpBatchLink({
        url: apiUrl,
        headers: () => {
          const token = getPetParentToken();
          return { authorization: token ? `Bearer ${token}` : '', timezone };
        },
      }),
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<PetAppRouter>({
  client: trpcClient,
  queryClient,
});

export { trpcClient };
